import type { TFunction } from 'i18next';

import type { YearSpend } from '../../products/productDetail/yearSpend';

/** Any figure's stand-in while the year loads, or once its read failed: that is not a year that cost nothing. */
export const pendingRead = (t: TFunction, spend: YearSpend): string | null => {
  if (spend.isLoading) return '…';
  return spend.failed ? t('productDashboard.spendReadFailed') : null;
};

/** What the team's own hours came to in one sprint, before a release, retention or adjustment moves it. */
const workAt = (spend: YearSpend, index: number): number =>
  spend.series.reduce((sum, each) => sum + (each.values[index] ?? 0), 0);

/** Hours logged there that no rate could price: a release or a fixed charge beside them is not their price. */
export const sprintUnpriced = (spend: YearSpend, sprintId: string, index: number): boolean =>
  !!spend.unratedBySprint?.[sprintId] && workAt(spend, index) === 0;

/** The sprint's budget, or nothing where its hours had no rate: the budget is priced at the same rates. */
export const sprintBudget = (spend: YearSpend, sprintId: string, index: number): number | null =>
  sprintUnpriced(spend, sprintId, index) ? null : (spend.budgetBySprint[sprintId] ?? null);

/** A money figure's stand-in: pending, or a dash where hours were logged and none of the work was priced. */
export const unpriced = (t: TFunction, spend: YearSpend): string | null => {
  const pending = pendingRead(t, spend);
  if (pending) return pending;
  // A fixed charge is priced work too: it is what that version bills for its hours.
  const anyWorkPriced =
    spend.sprints.some((_, index) => workAt(spend, index) !== 0) ||
    Object.values(spend.fixedBySprint ?? {}).some(amount => amount !== 0);
  // Hours logged and nothing priced reads as free work, so it is a dash, as the sprint report shows it.
  return !anyWorkPriced && Object.keys(spend.unratedBySprint ?? {}).length ? '—' : null;
};
